<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import MainPageHeader from '@/components/MainPage-header.vue'
import MainPageLeft from '@/components/MainPage-left.vue'
import BreadcrumbBar from '@/components/MainPage-breadcrumbbar.vue'
import { useTabsStore } from '@/stores/tabs'
import { useUserStore } from '@/stores/user'
import { useLayoutStore } from '@/stores/layout'

const asideWidth = ref('100px')
const mainWidth = computed(() => `calc(100% - ${asideWidth.value})`)
const route = useRoute()
const router = useRouter()
const tabsStore = useTabsStore()
const userStore = useUserStore()
const layoutStore = useLayoutStore()

// 判断是否为首页，首页不显示顶部Header和面包屑
const isHomePage = computed(() => route.name === 'Home')

// 仅在 Home 允许隐藏导航/头部；离开 Home 自动恢复，避免隐藏后无法操作其它页面
watch(
  () => route.name,
  (name) => {
    if (name !== 'Home') layoutStore.setChromeVisible(true)
  },
  { immediate: true },
)

// 进入新页面自动新增标签
watch(
  () => route.fullPath,
  () => {
    tabsStore.addByRoute(route)
  },
  { immediate: true },
)

// 点击标签切换时导航
watch(
  () => tabsStore.active,
  (name) => {
    if (name && name !== route.fullPath) {
      router.push(name)
    }
  },
)

// 进入主布局后初始化加载一次用户信息（已登录情况下）
onMounted(() => {
  userStore.fetchUserInfo().catch((err) => {
    console.warn('初始化用户信息失败：', err)
  })
})
</script>

<template>
  <div class="main-layout">
    <el-container class="container">
      <!-- 左侧菜单：首页仍然显示 -->
      <el-aside
        v-if="!isHomePage || layoutStore.chromeVisible"
        :style="{ width: asideWidth, height: '100%' }"
      >
        <MainPageLeft />
      </el-aside>

      <el-container class="content-container">
        <!-- Header：首页不显示 -->
        <el-header
          v-if="!isHomePage && layoutStore.chromeVisible"
          height="48px"
          :style="{ width: '100%', padding: '0', maxWidth: '100%', boxSizing: 'border-box' }"
        >
          <MainPageHeader
            :style="{ width: '100%', margin: '0', boxSizing: 'border-box' }"
            :aside-width="asideWidth"
            :main-width="mainWidth"
          />
        </el-header>

        <!-- 面包屑导航：首页不显示 -->
        <el-header
          v-if="!isHomePage && layoutStore.chromeVisible"
          height="44px"
          :style="{ width: '100%', padding: '0' }"
        >
          <BreadcrumbBar />
        </el-header>

        <el-main id="main-scroller" :style="{ width: '100%', height: '100%' }">
          <!-- KeepAlive 使用组件 include 列表做缓存控制，同步 tabsStore.cachedViews -->
          <router-view v-slot="{ Component, route }">
            <keep-alive :include="tabsStore.cachedViews" :max="12">
              <component :is="Component" :key="route.fullPath" />
            </keep-alive>
          </router-view>
        </el-main>
      </el-container>
    </el-container>
  </div>
</template>

<style scoped>
.container {
  flex-direction: row;
  height: 100vh;
}

.main-layout {
  height: 100vh;
}

.el-header {
  padding: 0;
  background: #fff;
}

/* 清除el-header可能的默认样式 */
:deep(.el-header) {
  padding: 0 !important;
  margin: 0 !important;
  border: none !important;
}
:deep(.header-row) {
  width: 100% !important;
  box-sizing: border-box !important;
}

.content-container {
  height: 100%;
  display: flex;
  flex: 1;
  min-height: 0; /* 允许内部flex子项成为滚动容器 */
}

.el-aside {
  background: #fff;
  height: 100%;
  flex-shrink: 0;
}

.el-main {
  padding: 0; /* 关键：移除默认内边距 */
  width: 100%; /* 强制占满父容器宽度 */
  max-width: none;
  flex: 1;
  overflow-y: auto; /* 允许内容在主区域滚动 */
  -webkit-overflow-scrolling: touch;
  min-height: 0; /* 配合flex使内部可以滚动 */
  display: flex;
  flex-direction: column;
}

/* 固定宽度样式 */
.el-main.fixed-width {
  flex: none;
  width: 800px;
}

/* 最小宽度样式 */
.el-main.min-width {
  min-width: 600px;
}

/* 标签栏样式 */
.tabs-row {
  display: flex;
  align-items: center;
  padding: 5px 12px;
  overflow: hidden;
  border-top: 1px solid #f0f0f0;
}
:deep(.el-tabs__nav-wrap) {
  max-width: 100%;
}
:deep(.el-tabs__nav-scroll) {
  overflow: hidden auto; /* 横向滚动 */
}
</style>
